import {
  BrowserCacheLocation,
  PublicClientApplication,
  SilentRequest,
  EventType,
} from '@azure/msal-browser';

const authClientId = process.env.NEXT_PUBLIC_AUTH_CLIENT_ID || "7b9e13f1-e1b5-4be3-a53e-b990d954b539";
// Replace with your actual BP tenant ID (get from Azure Portal)
const authTenantId = process.env.NEXT_PUBLIC_AUTH_TENANT_ID || "ea80952e-a476-42d4-aaf4-5457852b0f7e";
const authRedirectUri = process.env.NEXT_PUBLIC_AUTH_REDIRECT_URI || (typeof window !== 'undefined' ? window.location.origin : '');

const msalConfigAuthAuthority = `https://login.microsoftonline.com/${authTenantId}`;

export const msalConfig = {
  auth: {
    authority: msalConfigAuthAuthority,
    clientId: authClientId,
    redirectUri: authRedirectUri,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: false,
  },
};

const getInstance = (): PublicClientApplication | null => {
  try {
    return new PublicClientApplication(msalConfig);
  } catch (e) {
    return null;
  }
};

export const msalInstance = getInstance();

export enum ADScope {
  AISearchApi,
  UserRead,
  SharePoint,
}

export const scope = (adScope: ADScope): string => {
  const scopeId = authClientId;
  if (adScope === ADScope.AISearchApi) {
    return `api://${scopeId}/access_as_user`;
  } else if (adScope === ADScope.SharePoint) {
    return 'https://graph.microsoft.com/Files.Read.All https://graph.microsoft.com/Sites.Read.All';
  }
  return 'https://graph.microsoft.com/User.Read';
};

export const acquireAccessToken = async (adScope: ADScope): Promise<string> => {
  if (process.env.NEXT_PUBLIC_E2E) {
    return process.env.NEXT_PUBLIC_E2E_BEARER_TOKEN || "test-token";
  }
  if (!msalInstance) {
    return '';
  }

  // Ensure we have an active account
  let account = msalInstance.getActiveAccount();
  if (!account) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts && accounts.length > 0) {
      account = accounts[0];
      msalInstance.setActiveAccount(account);
    } else {
      return '';
    }
  }

  const request: SilentRequest = {
    scopes: scope(adScope).split(' '),
    account: account,
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (_) {
    try {
      await msalInstance.acquireTokenRedirect(request);
    } catch (redirectError) {
      console.error('MSAL: Token acquisition redirect failed', redirectError);
    }
  }
  return '';
};

/**
 * Silent-only token acquisition. Returns "" on any failure and — unlike
 * {@link acquireAccessToken} — NEVER triggers an interactive redirect. Callers
 * that must control the re-auth flow (e.g. the deep-link handler, which has to
 * return the user to the original deep link rather than "/") use this and then
 * decide whether to call {@link reauthRedirect}. Feature #10286672.
 */
export const acquireAccessTokenSilent = async (adScope: ADScope): Promise<string> => {
  if (process.env.NEXT_PUBLIC_E2E) {
    return process.env.NEXT_PUBLIC_E2E_BEARER_TOKEN || "test-token";
  }
  if (!msalInstance) {
    return '';
  }

  let account = msalInstance.getActiveAccount();
  if (!account) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts && accounts.length > 0) {
      account = accounts[0];
      msalInstance.setActiveAccount(account);
    } else {
      return '';
    }
  }

  const request: SilentRequest = {
    scopes: scope(adScope).split(' '),
    account: account,
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch {
    return '';
  }
};

/**
 * Drive an interactive redirect to re-acquire a token, returning the user to
 * `returnUrl` (defaults to the current location) once auth completes. Used by
 * the deep-link handler so an expired/blocked silent token (e.g. Safari ITP
 * blocking MSAL's silent-renewal iframe) re-authenticates *in place* instead of
 * bouncing to the home page. Feature #10286672.
 *
 * Returns `true` when an interactive redirect was actually initiated (the page
 * is about to navigate away) and `false` when no redirect could be started —
 * e.g. MSAL is unavailable or `acquireTokenRedirect` rejects. Callers use the
 * result to surface an inline error instead of leaving the user on a loading
 * state forever.
 */
export const reauthRedirect = async (
  adScope: ADScope,
  returnUrl?: string,
): Promise<boolean> => {
  if (!msalInstance) {
    return false;
  }
  const account =
    msalInstance.getActiveAccount() ||
    msalInstance.getAllAccounts()[0] ||
    undefined;
  try {
    await msalInstance.acquireTokenRedirect({
      scopes: scope(adScope).split(' '),
      account,
      redirectStartPage:
        returnUrl ??
        (typeof window !== 'undefined' ? window.location.href : undefined),
    });
    return true;
  } catch (e) {
    console.error('MSAL: re-auth redirect failed', e);
    return false;
  }
};

export const signOutClickHandler = (instance: PublicClientApplication) => {
  // Local sign out only - doesn't sign out from Microsoft globally
  instance.clearCache();
  instance.setActiveAccount(null);
};

export const signOutGlobally = (instance: PublicClientApplication) => {
  const logoutRequest = {
    account: instance.getActiveAccount(),
    postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '',
  };
  instance.logoutRedirect(logoutRequest);
};
